import { Button, ErrorText, Input, Label } from '@cluey/cluey-components';
import { zodResolver } from '@hookform/resolvers/zod';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router';
import { z } from 'zod';

import { PATHS, useSignupStore } from '.';
import { api } from '../../../../api';
import alertExclamation from '../../../../assets/images/icon-filled-alert-exclamation-circle.svg';
import { AlertExclamationIcon } from '../../../../components/ErrorText/AlertExclamationIcon';
import { FullPageLoader } from '../../../../components/Loader/Loader';
import SelectDropdown from '../../../../components/SelectDropdown/SelectDropdown';
import type { SelectDropdownOption } from '../../../../components/SelectDropdown/SelectDropdown';
import { PlusIcon } from '../../../../icons';
import { PATH_HUB_HOME, PATH_HUB_LOG_IN } from '../../../../util/pagePath';
import { Checkbox } from '../../../../v2/components/ui/checkbox';
import RightColumn from '../RightColumn';
import TwoColumnLayout from '../TwoColumnLayout';
import { InlineLoader } from '../common';
import useNavigateWithSearch from '../useNavigateWithSearch';
import { SIGNUP_QUERY_KEYS_NOT_IN_TRACKING } from './SignUpPage';
import { useReviewDetailsStore } from './SignUpReviewDetailsPage';
import Stepper from './Stepper';
import ParentEmailNotice from './components/ParentEmailNotice';
import prepareSignUpPayload, { NZ_POSTCODE, NZ_STATE } from './prepareSignUpPayload';
import useSearchParam from './useSearchParam';
import useSignout from './useSignout';

const UserType = z.enum(['Parent / Guardian', 'Student', 'Other']);
const CountryCode = z.enum(['AU', 'NZ']);

const signingUpForOptions = (continueChildSignup: boolean) =>
  continueChildSignup
    ? [
        {
          text: 'My child / another child I care for',
          value: UserType.Values['Parent / Guardian'],
        },
      ]
    : [
        {
          text: 'Myself',
          value: UserType.Values.Student,
        },
        {
          text: 'My child / another child I care for',
          value: UserType.Values['Parent / Guardian'],
        },
      ];

const getCountryOptions = (signUpUserCountry: string) => {
  if (signUpUserCountry === 'NZ') {
    return [
      {
        text: 'New Zealand',
        value: 'NZ',
      },
    ];
  } else {
    return [
      {
        text: 'Australia',
        value: 'AU',
      },
    ];
  }
};

const AUS_STATE_OPTIONS: SelectDropdownOption[] = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'].map((key) => ({
  text: key,
  value: key,
}));

const SCHOOL_YEAR_OPTIONS: { text: string; value: string; country: string[] }[] = [
  { text: 'Year 2', value: 'Year 2', country: ['AU'] },
  { text: 'Year 3', value: 'Year 3', country: ['AU', 'NZ'] },
  { text: 'Year 4', value: 'Year 4', country: ['AU', 'NZ'] },
  { text: 'Year 5', value: 'Year 5', country: ['AU', 'NZ'] },
  { text: 'Year 6', value: 'Year 6', country: ['AU', 'NZ'] },
  { text: 'Year 7', value: 'Year 7', country: ['AU', 'NZ'] },
  { text: 'Year 8', value: 'Year 8', country: ['AU', 'NZ'] },
  { text: 'Year 9', value: 'Year 9', country: ['AU', 'NZ'] },
  { text: 'Year 10', value: 'Year 10', country: ['AU', 'NZ'] },
  { text: 'Year 11', value: 'Year 11', country: ['AU', 'NZ'] },
  { text: 'Year 12', value: 'Year 12', country: ['AU'] },
];

const needsParentConsentForYear = (year: string | null | undefined, isNZ?: boolean): boolean => {
  const parsedYear = year ? parseInt(year.split(' ')[1]) : null;

  if (isNZ) {
    return parsedYear ? parsedYear < 11 : false;
  }
  return parsedYear ? parsedYear < 10 : false;
};

const ParentDetailsForm = z.object({
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  mobileNumber: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
});
type ParentDetailsForm = z.infer<typeof ParentDetailsForm>;

const StudentEntryForm = z.object({
  firstName: z.string().nullable().optional(),
  schoolYear: z.string().nullable().optional(),
  countryCode: CountryCode.nullable().optional(),
  postCode: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
});
export type StudentEntryForm = z.infer<typeof StudentEntryForm>;

const createStudentDetailsForm = (signupUserEmail: string | undefined) =>
  z
    .object({
      signingUpFor: z.union([UserType, z.null()]).refine((v) => v != null, { message: 'Select an option' }),
      countryCode: CountryCode.nullable().optional(),
      postCode: z.string().nullable().optional(),
      state: z.string().nullable().optional(),
      schoolYear: z.string().nullable().optional(),
      parentDetails: ParentDetailsForm.nullable().optional(),
      students: z.array(StudentEntryForm).optional(),
    })
    .superRefine((data, ctx) => {
      if (data.signingUpFor === 'Student') {
        // Only validate displayed fields: countryCode, schoolYear; postCode/state only when AU
        if (!data.countryCode) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Select country',
            path: ['countryCode'],
          });
        }
        if (data.countryCode === 'AU') {
          if (!data.postCode?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Postcode required',
              path: ['postCode'],
            });
          }
          if (!data.state) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Select state',
              path: ['state'],
            });
          }
        }
        if (!data.schoolYear) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Select school year',
            path: ['schoolYear'],
          });
        }

        // When school year has parentConsent: require parent details
        if (needsParentConsentForYear(data.schoolYear, data.countryCode === 'NZ')) {
          const pd = data.parentDetails;
          if (!pd?.firstName?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'First name required',
              path: ['parentDetails', 'firstName'],
            });
          }
          if (!pd?.lastName?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Last name required',
              path: ['parentDetails', 'lastName'],
            });
          }
          if (!pd?.mobileNumber?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Mobile number required',
              path: ['parentDetails', 'mobileNumber'],
            });
          }
          if (!pd?.email?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Email required',
              path: ['parentDetails', 'email'],
            });
          } else if (pd?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pd.email)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Please enter a valid email address',
              path: ['parentDetails', 'email'],
            });
          } else if (
            pd?.email?.trim() &&
            signupUserEmail &&
            pd.email.trim().toLowerCase() === signupUserEmail.trim().toLowerCase()
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Parent/guardian email must be different from your sign-up email',
              path: ['parentDetails', 'email'],
            });
          }
        }
        return;
      }

      if (data.signingUpFor === 'Parent / Guardian') {
        const students = data.students ?? [];
        if (students.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Add at least one student',
            path: ['students'],
          });
        }
        students.forEach((student, i) => {
          if (!student?.firstName?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Student's first name required",
              path: ['students', i, 'firstName'],
            });
          }
          if (!student?.schoolYear) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Select school year',
              path: ['students', i, 'schoolYear'],
            });
          }
          if (!student?.countryCode) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Select country',
              path: ['students', i, 'countryCode'],
            });
          }
          if (student?.countryCode === 'AU') {
            if (!student?.postCode?.trim()) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Postcode required',
                path: ['students', i, 'postCode'],
              });
            }
            if (!student?.state) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Select state',
                path: ['students', i, 'state'],
              });
            }
          }
        });
      }
    });

export type StudentDetailsForm = z.infer<ReturnType<typeof createStudentDetailsForm>>;

/**
 * Returns the first error path in document order for scroll-to-error UX.
 * @param {Record<string, unknown>} errors - Form errors from react-hook-form
 * @returns {string | null} The first error path (e.g. 'signingUpFor', 'students.0.firstName'), or null
 */
function getFirstErrorPath(errors: Record<string, unknown>): string | null {
  const get = (obj: unknown, path: string): unknown =>
    path.split('.').reduce((o, p) => (o as Record<string, unknown>)?.[p], obj);
  const hasError = (obj: unknown): boolean => obj != null && typeof obj === 'object' && 'message' in obj;

  const topLevelOrder = [
    'signingUpFor',
    'countryCode',
    'postCode',
    'state',
    'schoolYear',
    'parentDetails.firstName',
    'parentDetails.lastName',
    'parentDetails.mobileNumber',
    'parentDetails.email',
    'students',
  ];
  for (const path of topLevelOrder) {
    if (hasError(get(errors, path))) return path;
  }
  const students = errors.students;
  if (students && Array.isArray(students)) {
    const studentFieldOrder = ['firstName', 'schoolYear', 'countryCode', 'postCode', 'state'];
    for (let i = 0; i < students.length; i++) {
      const s = students[i];
      if (s && typeof s === 'object') {
        for (const field of studentFieldOrder) {
          const path = `students.${i}.${field}`;
          if (hasError(get(errors, path))) return path;
        }
      }
    }
  }
  return null;
}

export const SignUpStudentDetails = () => {
  const continueChildSignup = useSearchParam('continueChildSignup') === 'true';
  const { formValues } = useReviewDetailsStore();
  const { email: signupStoreEmail } = useSignupStore(({ email }) => ({ email }));
  const { data: accountDetails, isLoading, error: fetchAccountDetailsError, refetch } = api.account.details.useQuery();
  const signUpUserCountry = accountDetails?.country;
  const countryOptions = getCountryOptions(signUpUserCountry);
  const history = useHistory();
  const navigateWithSearch = useNavigateWithSearch();
  const signupUserEmail = signupStoreEmail || accountDetails?.email;
  const signout = useSignout();
  const studentDetailsSchema = useMemo(() => createStudentDetailsForm(signupUserEmail), [signupUserEmail]);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const tracking = Object.fromEntries(
    [...params.entries()].filter(([key]) => !SIGNUP_QUERY_KEYS_NOT_IN_TRACKING.has(key))
  );
  const cleanedTrackingEntries = Object.entries({
    fbclid: tracking.fbclid,
    gclid: tracking.gclid,
    last_adgroup: tracking.last_adgroup,
    last_adgroup_id: tracking.last_adgroup_id != null ? String(tracking.last_adgroup_id) : undefined,
    last_campaign: tracking.last_campaign,
    last_campaign_id: tracking.last_campaign_id != null ? String(tracking.last_campaign_id) : undefined,
    last_offer: tracking.last_offer,
    last_source: tracking.last_source,
  }).filter((entry): entry is [string, string] => entry[1] != null);
  const trackingPayload =
    cleanedTrackingEntries.length > 0
      ? (Object.fromEntries(cleanedTrackingEntries) as { [key: string]: string })
      : undefined;
  const defaultValues = formValues
    ? formValues
    : {
        signingUpFor: continueChildSignup ? UserType.Values['Parent / Guardian'] : null,
        countryCode: null,
        postCode: null,
        state: null,
        schoolYear: null,
        parentDetails: {
          firstName: null,
          lastName: null,
          mobileNumber: null,
          email: null,
        },
        students: [{ firstName: null, schoolYear: null, countryCode: null, postCode: null, state: null }],
      };

  const {
    watch,
    setValue,
    handleSubmit,
    control,
    formState: { errors, isSubmitted },
  } = useForm<StudentDetailsForm>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: zodResolver(studentDetailsSchema),
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'students' });

  const [showLocationForStudentId, setShowLocationForStudentId] = useState<Record<string, boolean>>({});

  const [signingUpFor, countryCode, postCode, state, schoolYear, parentDetails, students] = watch([
    'signingUpFor',
    'countryCode',
    'postCode',
    'state',
    'schoolYear',
    'parentDetails',
    'students',
  ]);

  const needsParentConsent = needsParentConsentForYear(schoolYear, countryCode === 'NZ');

  const schoolYearOptions = useMemo((): SelectDropdownOption[] => {
    if (countryCode === 'NZ') {
      return SCHOOL_YEAR_OPTIONS.filter((option) => option.country.includes('NZ')).map((option) => ({
        text: option.text,
        value: option.value,
      }));
    }
    return SCHOOL_YEAR_OPTIONS.filter((option) => option.country.includes('AU')).map((option) => ({
      text: option.text,
      value: option.value,
    }));
  }, [countryCode]);

  const getSchoolYearOptionsForCountry = (country: string | null | undefined): SelectDropdownOption[] => {
    if (country === 'NZ') {
      return SCHOOL_YEAR_OPTIONS.filter((option) => option.country.includes('NZ')).map((option) => ({
        text: option.text,
        value: option.value,
      }));
    }
    return SCHOOL_YEAR_OPTIONS.filter((option) => option.country.includes('AU')).map((option) => ({
      text: option.text,
      value: option.value,
    }));
  };

  const scrollToFirstError = (errors: Record<string, unknown>) => {
    const path = getFirstErrorPath(errors);
    if (path) {
      const el = document.querySelector(`[data-error-field="${path}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };
  // User has already finished signing up, redirect to home
  useEffect(() => {
    if (accountDetails?.userType === 'Account') {
      history.push(PATH_HUB_HOME);
    }
  }, [accountDetails?.userType, accountDetails?.status, history, signout, navigateWithSearch]);

  const { mutate: updateLeadRecord, isLoading: updatingLeadRecord } = api.account.update.useMutation();
  const onUpdateLeadRecordSuccess = () => {
    api.account.details.invalidateQueries();

    navigateWithSearch(PATHS.REVIEW_DETAILS_PATH);
  };

  const [hasSignedUpForUnderagedChild, setHasSignedUpForUnderagedChild] = useState(false);
  const [signUpForUnderagedChildError, setSignUpForUnderagedChildError] = useState<Error | null>(null);
  const { mutate: signUpForUnderagedChild } = api.clueyPlus.signUpForUnderagedChild.useMutation({
    onSuccess: () => {
      setHasSignedUpForUnderagedChild(true);
      setIsSigningUpForUnderagedChild(false);
      signout();
    },
    onError: (error, params) => {
      if ((error as Error).message.includes('already exists')) {
        setSignUpForUnderagedChildError(
          new Error(
            `A request has already been submitted for email ${params?.email} or mobile number ${params?.mobile}.`
          )
        );
      } else {
        setSignUpForUnderagedChildError(error as Error);
      }
      setIsSigningUpForUnderagedChild(false);
    },
  });

  const handleOnSubmit = handleSubmit(
    (data: StudentDetailsForm) => {
      const isParent = signingUpFor === UserType.Values['Parent / Guardian'];
      const payload = prepareSignUpPayload(isParent, accountDetails, data);
      const isNZ = data.countryCode === 'NZ';

      useReviewDetailsStore.setState({
        signUpUser: payload.signUpUser,
        isParent,
        students: payload.students,
        formValues: data,
      });
      if (isParent) {
        updateLeadRecord(
          {
            customerType: accountDetails?.userType || 'Lead', // Assume Lead if no user type
            recordType: signingUpFor,
            curriculum: isNZ ? NZ_STATE : data.students[0].state,
            yearLevel: data.students[0].schoolYear,
            postCode: isNZ ? NZ_POSTCODE : data.students[0].postCode,
            leadSource: 'ClueyPlus Signup',
            ...(trackingPayload ? { tracking: trackingPayload } : {}),
          },
          { onSuccess: onUpdateLeadRecordSuccess }
        );
      } else {
        updateLeadRecord(
          {
            customerType: accountDetails?.userType || 'Lead', // Assume Lead if no user type
            recordType: signingUpFor,
            curriculum: isNZ ? NZ_STATE : data.state,
            yearLevel: data.schoolYear,
            postCode: isNZ ? NZ_POSTCODE : data.postCode,
            leadSource: 'ClueyPlus Signup',
            ...(trackingPayload ? { tracking: trackingPayload } : {}),
          },
          { onSuccess: onUpdateLeadRecordSuccess }
        );
      }
    },
    (errors) => {
      scrollToFirstError(errors);
    }
  );
  const renderErrorIcon = (message: string) => {
    return (
      <div className="flex flex-row items-start gap-1">
        <img src={alertExclamation} alt="error-icon" width={16} height={16} />
        <span className="text-sm font-bold !text-red-5">{message}</span>
      </div>
    );
  };

  const [isSigningUpForUnderagedChild, setIsSigningUpForUnderagedChild] = useState(false);
  const handleOnSubmitForUnderagedChild = handleSubmit((data: StudentDetailsForm) => {
    const isNZ = data.countryCode === 'NZ';
    setIsSigningUpForUnderagedChild(true);
    updateLeadRecord(
      {
        customerType: accountDetails?.userType || 'Lead', // Assume Lead if no user type
        recordType: signingUpFor,
        curriculum: isNZ ? 'NZ' : data.state,
        yearLevel: data.schoolYear,
        leadSource: 'ClueyPlus Signup',
      },
      {
        onSuccess: () => {
          setTimeout(() => {
            signUpForUnderagedChild({
              name: `${data.parentDetails?.firstName} ${data.parentDetails?.lastName}`,
              firstName: data.parentDetails?.firstName || '',
              lastName: data.parentDetails?.lastName || '',
              email: data.parentDetails?.email,
              mobile: data.parentDetails?.mobileNumber,
              leadId: accountDetails?.customerSfid,
              countryCode: accountDetails?.country,
            });
          }, 500);
        },
        onError: (error) => {
          console.error(error);
          setIsSigningUpForUnderagedChild(false);
        },
      }
    );
  });

  const prevCountryCodeRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (signUpUserCountry) {
      setValue('countryCode', signUpUserCountry);
      setValue('students.0.countryCode', signUpUserCountry);
    }
  }, [signUpUserCountry, setValue, students]);

  useEffect(() => {
    const prevCountryCode = prevCountryCodeRef.current;
    prevCountryCodeRef.current = countryCode;

    // Only reset when country actually changed (not on initial mount, to preserve formValues from back navigation)
    if (prevCountryCode !== undefined && prevCountryCode !== countryCode) {
      setValue('postCode', null);
      setValue('state', null);
      setValue('schoolYear', null);
    }
  }, [countryCode, setValue]);

  const isSubmitting = updatingLeadRecord || isSigningUpForUnderagedChild;
  if (isLoading) {
    return <FullPageLoader />;
  }

  const fetchAccountDetailsErrorColumn = () => {
    return (
      <div className="ml-auto mr-auto box-content flex h-full max-w-[420px] flex-col px-5">
        <div className="mb-6 mt-[92px]">
          <Stepper steps={5} activeStep={3} />
        </div>
        <div className="mb-6 flex flex-row">
          <AlertExclamationIcon height={20} width={20} className="mr-2 mt-0.5 fill-red-5" />
          <ErrorText className="!text-base">
            There was a problem retrieving some information. Try again or log in to your account before proceeding.
          </ErrorText>
        </div>
        <Button size="sm" className="w-full" onClick={() => refetch()}>
          Try again
        </Button>

        <Button
          appearance="reverse"
          size="sm"
          className="mt-4 w-full"
          onClick={() => {
            signout({ hardSignout: true });
            navigateWithSearch(PATH_HUB_LOG_IN);
          }}
        >
          Log in
        </Button>
      </div>
    );
  };

  const renderLeftColumn = () => {
    if (accountDetails?.status === 'Closed') {
      return (
        <div className="ml-auto mr-auto box-content flex h-full max-w-[420px] flex-col px-5">
          <div className="mb-6 mt-12">
            <Stepper steps={5} activeStep={5} />
          </div>
          <ParentEmailNotice email={parentDetails?.email ?? 'your parent/guardian'} />

          <Button
            appearance="reverse"
            size="sm"
            className="mt-10 w-full"
            onClick={() => {
              signout({ hardSignout: true });
              navigateWithSearch(PATH_HUB_LOG_IN);
            }}
          >
            Go to Cluey Hub
          </Button>
        </div>
      );
    }
    return (
      <div className="ml-auto mr-auto box-content flex h-full max-w-[420px] flex-col px-5">
        <div className="mb-6 mt-12">
          <Stepper steps={5} activeStep={3} />
        </div>
        <h2 className="mb-4 font-display text-4xl font-bold">Student details</h2>
        <p className="max-w-md pb-4 text-lg">Tell us about who you&apos;re signing up.</p>
        <div className={classNames('flex w-full flex-col gap-y-5')}>
          <div
            className={classNames('w-full flex-col gap-1', continueChildSignup ? 'hidden' : 'flex')}
            data-error-field="signingUpFor"
          >
            <Label id="signing-up-for" size="base">
              Who are you signing up today?
            </Label>
            <SelectDropdown
              options={signingUpForOptions(continueChildSignup)}
              value={signingUpFor}
              onChange={(value: string) =>
                setValue('signingUpFor', value as z.infer<typeof UserType>, { shouldValidate: isSubmitted })
              }
              placeholder=""
              className="w-full"
              buttonClassName={classNames(
                'w-full text-left border border-grey-3 !px-3 rounded',
                errors.signingUpFor && '!bg-red-1 !border-red-5 !border-2'
              )}
              labelClassName="w-full !font-normal !text-base"
              iconFill="blue-5"
              optionClassName="!p-3"
            />
            {errors.signingUpFor && renderErrorIcon(errors.signingUpFor.message)}
            {signingUpFor === UserType.Values.Student && (
              <div className="flex flex-col gap-1 text-sm text-grey-6">
                You must be at least 16 years old to sign up, otherwise a parent or guardian will be required to sign up
                for you.
              </div>
            )}
          </div>
          {signingUpFor === UserType.Values.Student && (
            <>
              <div className="hidden w-full flex-col gap-1" data-error-field="countryCode">
                <Label id="country-code" size="base">
                  Country
                </Label>
                <SelectDropdown
                  options={countryOptions}
                  value={countryCode}
                  onChange={(value: z.infer<typeof CountryCode>) =>
                    setValue('countryCode', value, { shouldValidate: isSubmitted })
                  }
                  placeholder=""
                  className="w-full"
                  buttonClassName={classNames(
                    'w-full text-left border border-grey-3 !px-3 rounded',
                    errors.countryCode && '!bg-red-1 !border-red-5 !border-2'
                  )}
                  labelClassName="w-full !font-normal !text-base"
                  iconFill="blue-5"
                  optionClassName="!p-3"
                />
                {errors.countryCode && renderErrorIcon(errors.countryCode.message)}
              </div>
              {countryCode === 'AU' && (
                <div className="flex w-full flex-col gap-y-5 lg:flex-row lg:gap-x-3">
                  <div className="flex w-full flex-col gap-1" data-error-field="postCode">
                    <Label id="postCode" size="base">
                      Postcode
                    </Label>
                    <Input
                      id="postCode"
                      value={postCode}
                      onChange={(value) => setValue('postCode', value, { shouldValidate: isSubmitted })}
                      placeholder=""
                      className={classNames('h-10 w-full', errors.postCode && '!border-2 !border-red-5 !bg-red-1')}
                    />
                    {errors.postCode && renderErrorIcon(errors.postCode.message)}
                  </div>
                  <div className="flex w-full flex-col gap-1" data-error-field="state">
                    <Label id="state" size="base">
                      State
                    </Label>
                    <SelectDropdown
                      options={AUS_STATE_OPTIONS}
                      value={state}
                      onChange={(value: string) => setValue('state', value, { shouldValidate: isSubmitted })}
                      placeholder=""
                      className="w-full"
                      buttonClassName={classNames(
                        'w-full text-left border border-grey-3 !px-3 rounded',
                        errors.state && '!bg-red-1 !border-red-5 !border-2'
                      )}
                      labelClassName="w-full !font-normal !text-base"
                      iconFill="blue-5"
                      optionClassName="!p-3"
                    />
                    {errors.state && renderErrorIcon(errors.state.message)}
                  </div>
                </div>
              )}
              <div className="flex w-full flex-col gap-1" data-error-field="schoolYear">
                <Label id="school-year" size="base">
                  School year
                </Label>
                <SelectDropdown
                  options={schoolYearOptions}
                  value={schoolYear}
                  onChange={(value: string) => setValue('schoolYear', value, { shouldValidate: isSubmitted })}
                  placeholder=""
                  className="w-full"
                  buttonClassName={classNames(
                    'w-full text-left border border-grey-3 !px-3 rounded',
                    errors.schoolYear && '!bg-red-1 !border-red-5 !border-2'
                  )}
                  labelClassName="w-full !font-normal !text-base"
                  iconFill="blue-5"
                  optionClassName="!p-3"
                />
                {errors.schoolYear && renderErrorIcon(errors.schoolYear.message)}
              </div>
              {needsParentConsent && (
                <>
                  <div className="rounded-lg bg-yellow-1 px-6 py-5">
                    <div className="mb-5 flex w-full items-start gap-3 lg:mb-0">
                      <div>
                        <AlertExclamationIcon height={20} width={20} className="mt-0.5 fill-yellow-4" />
                      </div>
                      <div>
                        <h4 className="block pb-3 text-base font-bold leading-tight">
                          Parent or guardian permission needed
                        </h4>
                        <p className="mb-0">
                          You must be at least 16 years old to sign yourself up. If you're under 16, a parent or
                          guardian will complete the sign-up for you.
                        </p>
                      </div>
                    </div>
                  </div>
                  {hasSignedUpForUnderagedChild ? (
                    <ParentEmailNotice email={parentDetails?.email ?? ''} />
                  ) : (
                    <>
                      <h2 className="max-w-md font-display text-3xl leading-10">Parent or guardian details</h2>
                      <div className="flex w-full flex-col gap-y-5 lg:flex-row lg:gap-x-3">
                        <div className="flex w-full flex-col gap-1" data-error-field="parentDetails.firstName">
                          <Label id="parent-first-name" size="base">
                            First name
                          </Label>
                          <Input
                            id="parent-first-name"
                            value={parentDetails?.firstName}
                            onChange={(value) =>
                              setValue('parentDetails.firstName', value, { shouldValidate: isSubmitted })
                            }
                            placeholder=""
                            className={classNames(
                              'h-10 w-full',
                              errors.parentDetails?.firstName && '!border-2 !border-red-5 !bg-red-1'
                            )}
                          />
                          {errors.parentDetails?.firstName && renderErrorIcon(errors.parentDetails?.firstName.message)}
                        </div>
                        <div className="flex w-full flex-col gap-1" data-error-field="parentDetails.lastName">
                          <Label id="parent-last-name" size="base">
                            Last name
                          </Label>
                          <Input
                            id="parent-last-name"
                            value={parentDetails?.lastName}
                            onChange={(value) =>
                              setValue('parentDetails.lastName', value, { shouldValidate: isSubmitted })
                            }
                            placeholder=""
                            className={classNames(
                              'h-10 w-full',
                              errors.parentDetails?.lastName && '!border-2 !border-red-5 !bg-red-1'
                            )}
                          />
                          {errors.parentDetails?.lastName && renderErrorIcon(errors.parentDetails?.lastName.message)}
                        </div>
                      </div>
                      <div className="flex w-full flex-col gap-x-3" data-error-field="parentDetails.mobileNumber">
                        <Label id="parent-mobile-number" size="base">
                          Mobile number
                        </Label>
                        <Input
                          id="parent-mobile-number"
                          value={parentDetails?.mobileNumber}
                          onChange={(value) =>
                            setValue('parentDetails.mobileNumber', value, { shouldValidate: isSubmitted })
                          }
                          placeholder=""
                          className={classNames(
                            'h-10 w-full',
                            errors.parentDetails?.mobileNumber && '!border-2 !border-red-5 !bg-red-1'
                          )}
                        />
                        {errors.parentDetails?.mobileNumber &&
                          renderErrorIcon(errors.parentDetails?.mobileNumber.message)}
                      </div>
                      <div className="flex w-full flex-col gap-x-3" data-error-field="parentDetails.email">
                        <Label id="parent-email" size="base">
                          Email
                        </Label>
                        <Input
                          id="parent-email"
                          value={parentDetails?.email}
                          onChange={(value) => setValue('parentDetails.email', value, { shouldValidate: isSubmitted })}
                          placeholder=""
                          className={classNames(
                            'h-10 w-full',
                            errors.parentDetails?.email && '!border-2 !border-red-5 !bg-red-1'
                          )}
                        />
                        {errors.parentDetails?.email && renderErrorIcon(errors.parentDetails?.email.message)}
                        <div className="mt-1 flex flex-col gap-1 text-sm text-grey-6">
                          A request will be sent to your parent or guardian to sign up for you
                        </div>
                      </div>
                      {signUpForUnderagedChildError && (
                        <ErrorText>{(signUpForUnderagedChildError as Error).message}</ErrorText>
                      )}
                      <div>
                        <Button
                          disabled={isSubmitting}
                          size="sm"
                          className="flex w-full items-center justify-center"
                          onClick={handleOnSubmitForUnderagedChild}
                        >
                          {isSubmitting ? <InlineLoader className="" /> : 'Send request'}
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
          {signingUpFor === UserType.Values['Parent / Guardian'] && (
            <>
              {/* First student (fixed) */}
              {students?.[0] && (
                <>
                  {students.length > 1 && (
                    <h2 className="-mb-4 max-w-md font-display text-2xl leading-10">Student 1</h2>
                  )}
                  <div className="flex w-full flex-col gap-6 rounded-lg" data-error-field="students">
                    <div className="flex w-full flex-col gap-y-5 lg:flex-row lg:gap-x-3">
                      <div className="flex w-full flex-col gap-1" data-error-field="students.0.firstName">
                        <Label id="student-0-first-name" size="base">
                          Student&apos;s first name
                        </Label>
                        <Input
                          id="student-0-first-name"
                          value={students[0].firstName ?? ''}
                          onChange={(value) =>
                            setValue('students.0.firstName', value, { shouldValidate: isSubmitted, shouldTouch: true })
                          }
                          placeholder=""
                          className={classNames(
                            'h-10 w-full',
                            errors.students?.[0]?.firstName && '!border-2 !border-red-5 !bg-red-1'
                          )}
                        />
                        {errors.students?.[0]?.firstName && renderErrorIcon(errors.students[0].firstName.message)}
                      </div>
                      <div className="flex w-full flex-col gap-1" data-error-field="students.0.schoolYear">
                        <Label id="student-0-school-year" size="base">
                          School year
                        </Label>
                        <SelectDropdown
                          options={getSchoolYearOptionsForCountry(students[0].countryCode)}
                          value={students[0].schoolYear}
                          onChange={(value: string) =>
                            setValue('students.0.schoolYear', value, { shouldValidate: isSubmitted, shouldTouch: true })
                          }
                          placeholder=""
                          className="w-full"
                          buttonClassName={classNames(
                            'w-full text-left border border-grey-3 !px-3 rounded',
                            errors.students?.[0]?.schoolYear && '!bg-red-1 !border-red-5 !border-2'
                          )}
                          labelClassName="w-full !font-normal !text-base"
                          iconFill="blue-5"
                          optionClassName="!p-3"
                        />
                        {errors.students?.[0]?.schoolYear && renderErrorIcon(errors.students[0].schoolYear.message)}
                      </div>
                    </div>
                    <div className="flex w-full flex-col gap-x-3 gap-y-5">
                      <div className="hidden w-full flex-col gap-1" data-error-field="students.0.countryCode">
                        <Label id="student-0-country-code" size="base">
                          Country
                        </Label>
                        <SelectDropdown
                          options={countryOptions}
                          value={students[0].countryCode}
                          onChange={(value: z.infer<typeof CountryCode>) => {
                            setValue('students.0.countryCode', value, {
                              shouldValidate: isSubmitted,
                              shouldTouch: true,
                            });
                            setValue('students.0.postCode', null, { shouldValidate: isSubmitted, shouldTouch: true });
                            setValue('students.0.state', null, { shouldValidate: isSubmitted, shouldTouch: true });
                          }}
                          placeholder=""
                          className="w-full"
                          buttonClassName={classNames(
                            'w-full text-left border border-grey-3 !px-3 rounded',
                            errors.students?.[0]?.countryCode && '!bg-red-1 !border-red-5 !border-2'
                          )}
                          labelClassName="w-full !font-normal !text-base"
                          iconFill="blue-5"
                          optionClassName="!p-3"
                        />
                        {errors.students?.[0]?.countryCode && renderErrorIcon(errors.students[0].countryCode.message)}
                      </div>
                      {students[0].countryCode === 'AU' && (
                        <div className="flex w-full flex-col gap-y-5 lg:flex-row lg:gap-x-3">
                          <div className="flex w-full flex-col gap-1" data-error-field="students.0.postCode">
                            <Label id="student-0-postCode" size="base">
                              Postcode
                            </Label>
                            <Input
                              id="student-0-postCode"
                              value={students[0].postCode ?? ''}
                              onChange={(value) =>
                                setValue('students.0.postCode', value, {
                                  shouldValidate: isSubmitted,
                                  shouldTouch: true,
                                })
                              }
                              placeholder=""
                              className={classNames(
                                'h-10 w-full',
                                errors.students?.[0]?.postCode && '!border-2 !border-red-5 !bg-red-1'
                              )}
                            />
                            {errors.students?.[0]?.postCode && renderErrorIcon(errors.students[0].postCode.message)}
                          </div>
                          <div className="flex w-full flex-col gap-1" data-error-field="students.0.state">
                            <Label id="student-0-state" size="base">
                              State
                            </Label>
                            <SelectDropdown
                              options={AUS_STATE_OPTIONS}
                              value={students[0].state}
                              onChange={(value: string) =>
                                setValue('students.0.state', value, { shouldValidate: isSubmitted, shouldTouch: true })
                              }
                              placeholder=""
                              className="w-full"
                              buttonClassName={classNames(
                                'w-full text-left border border-grey-3 !px-3 rounded',
                                errors.students?.[0]?.state && '!bg-red-1 !border-red-5 !border-2'
                              )}
                              labelClassName="w-full !font-normal !text-base"
                              iconFill="blue-5"
                              optionClassName="!p-3"
                            />
                            {errors.students?.[0]?.state && renderErrorIcon(errors.students[0].state.message)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
              {/* Additional students */}
              {fields.slice(1).map((field, index) => {
                const i = index + 1;
                const student = students?.[i];
                return (
                  <div key={field.id} className="flex w-full flex-col gap-5">
                    <div className="-mb-4 flex items-center justify-between">
                      <h2 className="max-w-md font-display text-2xl leading-10">Student {i + 1}</h2>
                      <Button
                        type="button"
                        appearance="reverse"
                        size="xs"
                        onClick={() => remove(i)}
                        className="shrink-0"
                      >
                        REMOVE
                      </Button>
                    </div>
                    <div className="flex w-full flex-col gap-y-5 lg:flex-row lg:gap-x-3">
                      <div className="flex w-full flex-col gap-1" data-error-field={`students.${i}.firstName`}>
                        <Label id={`student-${i}-first-name`} size="base">
                          Student&apos;s first name
                        </Label>
                        <Input
                          id={`student-${i}-first-name`}
                          value={student?.firstName ?? ''}
                          onChange={(value) =>
                            setValue(`students.${i}.firstName`, value, {
                              shouldValidate: isSubmitted,
                              shouldTouch: true,
                            })
                          }
                          placeholder=""
                          className={classNames(
                            'h-10 w-full',
                            errors.students?.[i]?.firstName && '!border-2 !border-red-5 !bg-red-1'
                          )}
                        />
                        {errors.students?.[i]?.firstName && renderErrorIcon(errors.students[i].firstName.message)}
                      </div>
                      <div className="flex w-full flex-col gap-1" data-error-field={`students.${i}.schoolYear`}>
                        <Label id={`student-${i}-school-year`} size="base">
                          School year
                        </Label>
                        <SelectDropdown
                          options={getSchoolYearOptionsForCountry(student?.countryCode)}
                          value={student?.schoolYear}
                          onChange={(value: string) =>
                            setValue(`students.${i}.schoolYear`, value, {
                              shouldValidate: isSubmitted,
                              shouldTouch: true,
                            })
                          }
                          placeholder=""
                          className="w-full"
                          buttonClassName={classNames(
                            'w-full text-left border border-grey-3 !px-3 rounded',
                            errors.students?.[i]?.schoolYear && '!bg-red-1 !border-red-5 !border-2'
                          )}
                          labelClassName="w-full !font-normal !text-base"
                          iconFill="blue-5"
                          optionClassName="!p-3"
                        />
                        {errors.students?.[i]?.schoolYear && renderErrorIcon(errors.students[i].schoolYear.message)}
                      </div>
                    </div>
                    {signUpUserCountry !== 'NZ' && (
                      <div className="flex items-center gap-3 pt-2">
                        <Checkbox
                          id={`student-${i}-different-location`}
                          checked={showLocationForStudentId[field.id] ?? false}
                          onCheckedChange={() => {
                            const next = !(showLocationForStudentId[field.id] ?? false);
                            setShowLocationForStudentId((prev) => ({ ...prev, [field.id]: next }));
                            if (!next && students?.[0]) {
                              setValue(`students.${i}.countryCode`, students[0].countryCode, {
                                shouldValidate: isSubmitted,
                                shouldTouch: true,
                              });
                              setValue(`students.${i}.postCode`, students[0].postCode, {
                                shouldValidate: isSubmitted,
                                shouldTouch: true,
                              });
                              setValue(`students.${i}.state`, students[0].state, {
                                shouldValidate: isSubmitted,
                                shouldTouch: true,
                              });
                            }
                          }}
                        />
                        <label
                          htmlFor={`student-${i}-different-location`}
                          className="cursor-pointer text-base text-grey-6"
                        >
                          This student is based in a different location
                        </label>
                      </div>
                    )}
                    {(showLocationForStudentId[field.id] ?? false) && (
                      <div className="flex w-full flex-col gap-x-3 gap-y-5">
                        <div className="hidden w-full flex-col gap-1" data-error-field={`students.${i}.countryCode`}>
                          <Label id={`student-${i}-country-code`} size="base">
                            Country
                          </Label>
                          <SelectDropdown
                            options={countryOptions}
                            value={student?.countryCode}
                            onChange={(value: z.infer<typeof CountryCode>) => {
                              setValue(`students.${i}.countryCode`, value, {
                                shouldValidate: isSubmitted,
                                shouldTouch: true,
                              });
                              setValue(`students.${i}.postCode`, null, {
                                shouldValidate: isSubmitted,
                                shouldTouch: true,
                              });
                              setValue(`students.${i}.state`, null, {
                                shouldValidate: isSubmitted,
                                shouldTouch: true,
                              });
                            }}
                            placeholder=""
                            className="w-full"
                            buttonClassName={classNames(
                              'w-full text-left border border-grey-3 !px-3 rounded',
                              errors.students?.[i]?.countryCode && '!bg-red-1 !border-red-5 !border-2'
                            )}
                            labelClassName="w-full !font-normal !text-base"
                            iconFill="blue-5"
                            optionClassName="!p-3"
                          />
                          {errors.students?.[i]?.countryCode && renderErrorIcon(errors.students[i].countryCode.message)}
                        </div>
                        {student?.countryCode === 'AU' && (
                          <div className="flex w-full flex-col gap-y-5 lg:flex-row lg:gap-x-3">
                            <div className="flex w-full flex-col gap-1" data-error-field={`students.${i}.postCode`}>
                              <Label id={`student-${i}-postCode`} size="base">
                                Postcode
                              </Label>
                              <Input
                                id={`student-${i}-postCode`}
                                value={student?.postCode ?? ''}
                                onChange={(value) =>
                                  setValue(`students.${i}.postCode`, value, {
                                    shouldValidate: isSubmitted,
                                    shouldTouch: true,
                                  })
                                }
                                placeholder=""
                                className={classNames(
                                  'h-10 w-full',
                                  errors.students?.[i]?.postCode && '!border-2 !border-red-5 !bg-red-1'
                                )}
                              />
                              {errors.students?.[i]?.postCode && renderErrorIcon(errors.students[i].postCode.message)}
                            </div>
                            <div className="flex w-full flex-col gap-1" data-error-field={`students.${i}.state`}>
                              <Label id={`student-${i}-state`} size="base">
                                State
                              </Label>
                              <SelectDropdown
                                options={AUS_STATE_OPTIONS}
                                value={student?.state}
                                onChange={(value: string) =>
                                  setValue(`students.${i}.state`, value, {
                                    shouldValidate: isSubmitted,
                                    shouldTouch: true,
                                  })
                                }
                                placeholder=""
                                className="w-full"
                                buttonClassName={classNames(
                                  'w-full text-left border border-grey-3 !px-3 rounded',
                                  errors.students?.[i]?.state && '!bg-red-1 !border-red-5 !border-2'
                                )}
                                labelClassName="w-full !font-normal !text-base"
                                iconFill="blue-5"
                                optionClassName="!p-3"
                              />
                              {errors.students?.[i]?.state && renderErrorIcon(errors.students[i].state.message)}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="pb-2 pt-4">
                <Button
                  type="button"
                  size="sm"
                  appearance="reverse"
                  onClick={() =>
                    append({
                      firstName: null,
                      schoolYear: null,
                      countryCode: students?.[0]?.countryCode ?? null,
                      postCode: students?.[0]?.postCode ?? null,
                      state: students?.[0]?.state ?? null,
                    })
                  }
                  className="w-full"
                >
                  <p className="flex w-full items-center justify-center gap-2">
                    <PlusIcon className="h-4 w-4" />
                    <span>Add another student</span>
                  </p>
                </Button>
              </div>
            </>
          )}
          {!needsParentConsent && (
            <div>
              <Button
                disabled={isSubmitting}
                size="sm"
                className="flex w-full items-center justify-center"
                onClick={handleOnSubmit}
              >
                {isSubmitting ? <InlineLoader className="" /> : 'Next'}
              </Button>
            </div>
          )}
          <div className="mb-10" />
        </div>
      </div>
    );
  };

  const renderRightColumn = () => {
    return <RightColumn step={3} />;
  };

  return (
    <TwoColumnLayout
      renderLeftColumn={fetchAccountDetailsError ? fetchAccountDetailsErrorColumn : renderLeftColumn}
      renderRightColumn={renderRightColumn}
    />
  );
};
